/* CUSTOM CODE for RUGETTE -- START */
import { Component } from '@theme/component';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// setTimeout overflows (and fires immediately) above 2^31 - 1 ms, roughly 24.8 days.
const MAX_TIMEOUT = 2 ** 31 - 1;

// Small slack so a tick lands just after the boundary, never just before it.
const TICK_SLACK = 20;

const UNITS = /** @type {const} */ (['days', 'hours', 'minutes', 'seconds']);

/**
 * Timers that are on screen and still counting. They all share a single timeout aligned to the
 * wall clock, so N timers on a page cost one wake-up per second (or per minute), not N.
 *
 * @type {Set<CountdownTimer>}
 */
const activeTimers = new Set();
let tickTimeout = 0;

function scheduleTick() {
  clearTimeout(tickTimeout);
  tickTimeout = 0;

  if (!activeTimers.size || document.hidden) return;

  let step = MINUTE;
  for (const timer of activeTimers) {
    if (timer.step < step) step = timer.step;
  }

  // End times are always on a whole minute, so aligning to the epoch keeps every digit flip in
  // sync with the real remaining time and avoids the drift a plain setInterval accumulates.
  tickTimeout = window.setTimeout(tick, step - (Date.now() % step) + TICK_SLACK);
}

function tick() {
  const now = Date.now();
  for (const timer of activeTimers) timer.render(now);
  scheduleTick();
}

document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    clearTimeout(tickTimeout);
    tickTimeout = 0;
  } else {
    tick();
  }
});

/** Stops ticking for timers that are scrolled out of view. */
const visibilityObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      /** @type {CountdownTimer} */ (entry.target).setOnScreen(entry.isIntersecting);
    }
    scheduleTick();
  },
  { rootMargin: '200px 0px' }
);

/**
 * @param {number} value
 * @returns {string}
 */
const pad = (value) => String(value).padStart(2, '0');

/**
 * Returns the element's single text node, creating it if needed. Writing to `Text.data` is a
 * characterData mutation, which the Component base class does not observe. Setting `textContent`
 * would be a childList mutation and wake its MutationObserver on every tick.
 *
 * @param {HTMLElement} element
 * @returns {Text}
 */
function getTextNode(element) {
  const { firstChild } = element;
  if (firstChild instanceof Text && !firstChild.nextSibling) return firstChild;

  const node = document.createTextNode(element.textContent ?? '');
  element.replaceChildren(node);
  return node;
}

/**
 * @typedef {object} CountdownTimerRefs
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 */

/**
 * Counts down to `data-end` (ISO 8601; without an offset it is read in the visitor's local time
 * zone, with one it is a fixed moment). Once that moment passes, it keeps
 * counting for `data-extend-hours` more hours in the "extended" phase, then switches to "ended".
 *
 * The current phase is mirrored to `data-state` (running | extended | ended) and CSS handles what
 * is shown in each phase. Only digits that actually changed are written to the DOM.
 *
 * @extends {Component<CountdownTimerRefs>}
 */
class CountdownTimer extends Component {
  /** Tick interval in ms: one second, or one minute when seconds are hidden. */
  step = SECOND;

  #end = NaN;
  #finalEnd = NaN;
  #phaseTimeout = 0;

  /** @type {Partial<Record<(typeof UNITS)[number], Text>>} */
  #nodes = {};

  /** @type {Partial<Record<(typeof UNITS)[number], number>>} */
  #values = {};

  connectedCallback() {
    super.connectedCallback();

    this.#end = Date.parse(this.dataset.end ?? '');
    if (Number.isNaN(this.#end)) {
      console.warn('[countdown-timer] Invalid end date, expected YYYY-MM-DD and HH:MM:', this.dataset.end);
      return;
    }

    this.#finalEnd = this.#end + (Number(this.dataset.extendHours) || 0) * HOUR;

    for (const unit of UNITS) {
      const element = this.refs[unit];
      if (element) this.#nodes[unit] = getTextNode(element);
    }
    this.step = this.#nodes.seconds ? SECOND : MINUTE;

    const now = Date.now();
    this.#syncPhase(now);
    this.render(now);

    // Local time zone mode renders hidden on the server; reveal now that the digits are right.
    this.removeAttribute('data-pending');

    if (this.dataset.state !== 'ended') visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    visibilityObserver.unobserve(this);
    clearTimeout(this.#phaseTimeout);
    if (activeTimers.delete(this)) scheduleTick();
  }

  /**
   * @param {boolean} isOnScreen
   */
  setOnScreen(isOnScreen) {
    if (isOnScreen && this.dataset.state !== 'ended') {
      this.render(Date.now());
      activeTimers.add(this);
    } else {
      activeTimers.delete(this);
    }
  }

  /**
   * @param {number} now - Current time in ms
   */
  render(now) {
    const phase = this.#getPhase(now);
    if (phase !== this.dataset.state) this.#syncPhase(now);
    if (phase === 'ended') return;

    const target = phase === 'running' ? this.#end : this.#finalEnd;

    // Round up so the display never reads all zeros while the countdown is still live.
    let remaining = Math.ceil((target - now) / this.step) * this.step;

    // Without a days unit, hours keep counting past 24.
    const days = this.#nodes.days ? Math.floor(remaining / DAY) : 0;
    remaining -= days * DAY;
    const hours = Math.floor(remaining / HOUR);
    remaining -= hours * HOUR;
    const minutes = Math.floor(remaining / MINUTE);
    remaining -= minutes * MINUTE;
    const seconds = Math.floor(remaining / SECOND);

    this.#write('days', days);
    this.#write('hours', hours);
    this.#write('minutes', minutes);
    this.#write('seconds', seconds);
  }

  /**
   * @param {(typeof UNITS)[number]} unit
   * @param {number} value
   */
  #write(unit, value) {
    const node = this.#nodes[unit];
    if (!node || this.#values[unit] === value) return;

    this.#values[unit] = value;
    node.data = pad(value);
  }

  /**
   * @param {number} now
   * @returns {'running' | 'extended' | 'ended'}
   */
  #getPhase(now) {
    if (now < this.#end) return 'running';
    if (now < this.#finalEnd) return 'extended';
    return 'ended';
  }

  /**
   * Applies the current phase and arms a timeout for the next phase change. The timeout runs even
   * while the timer is off screen, so a hidden section is already gone when the user scrolls to it.
   *
   * @param {number} now
   */
  #syncPhase(now) {
    const phase = this.#getPhase(now);
    const previous = this.dataset.state;

    clearTimeout(this.#phaseTimeout);
    this.#phaseTimeout = 0;

    if (phase !== previous) this.dataset.state = phase;

    if (phase === 'ended') {
      activeTimers.delete(this);
      visibilityObserver.unobserve(this);
      this.#applyEndedBehavior();

      if (previous && previous !== 'ended') {
        this.dispatchEvent(new CustomEvent('countdown:ended', { bubbles: true }));
      }
      return;
    }

    const boundary = phase === 'running' ? this.#end : this.#finalEnd;
    const delay = Math.min(boundary - now + TICK_SLACK, MAX_TIMEOUT);

    this.#phaseTimeout = window.setTimeout(() => {
      const current = Date.now();
      this.#syncPhase(current);
      this.render(current);
    }, delay);
  }

  #applyEndedBehavior() {
    // Keep the section editable in the theme editor.
    if (this.dataset.endedBehavior !== 'hide_section' || window.Shopify?.designMode) return;

    const section = this.closest('.shopify-section');
    if (section instanceof HTMLElement) section.style.display = 'none';
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
/* CUSTOM CODE for RUGETTE -- END */
